import React, { useEffect, useMemo, useState, useCallback } from "react";
import { fetchAuthSession } from "@aws-amplify/auth";
import { listVendors, createVendor, updateVendor, deleteVendor } from "./api/vendors";
import VendorFilter from "./admin/VendorFilter";
import VendorForm, { cleanPayload } from "./admin/VendorForm";
import VendorTable from "./admin/VendorTable";
import VendorAnalyticsPanel from "./admin/VendorAnalyticsPanel";

const INITIAL_VENDOR = {
  name: "",
  address: "",
  description: "",
  owner: "",
  admin: "",
  manager: "",
  employee: "",
  active: true,
  google_maps_link: "",
  instagram: "",
  facebook: "",
  tiktok: "",
  X: "",
  telephone_number: "",
};

export default function AdminPage({ user, signOut }) {
  const [token, setToken] = useState("");
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const [creatingVendor, setCreatingVendor] = useState(INITIAL_VENDOR);
  const [editingVendor, setEditingVendor] = useState(null);
  const [originalVendor, setOriginalVendor] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showLogoModal, setShowLogoModal] = useState(false);
  const [logoVendor, setLogoVendor] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState("");
  const [logoSuccess, setLogoSuccess] = useState("");

  useEffect(() => {
    async function loadSession() {
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        if (idToken) setToken(idToken);
      } catch (err) {
        console.error("Error loading session:", err);
        setError("Unable to load session.");
      }
    }
    loadSession();
  }, [user]);

  const getToken = useCallback(async () => token, [token]);

  const refresh = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const data = await listVendors(getToken);
      setVendors(data);
    } catch (err) {
      console.error(err);
      setError("Error fetching vendors.");
    } finally {
      setLoading(false);
    }
  }, [getToken, token]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const summary = useMemo(() => {
    const total = vendors.length;
    const active = vendors.filter((v) => v.active !== false).length;
    const inactive = total - active;
    return { total, active, inactive };
  }, [vendors]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const items = vendors.filter((v) => {
      if (status === "active" && v.active === false) return false;
      if (status === "inactive" && v.active !== false) return false;
      if (!term) return true;
      return [
        v.name,
        v.owner,
        v.admin,
        v.manager,
        v.employee,
        v.description,
        v.google_maps_link,
        v.instagram,
        v.facebook,
        v.tiktok,
        v.X,
      ]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(term));
    });
    setPage(1);
    return items;
  }, [vendors, search, status]);

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const goToPage = (p) => setPage(Math.min(Math.max(1, p), totalPages));

  const handleCreate = async (e) => {
    e.preventDefault();
    setMessage("");
    try {
      const payload = cleanPayload(creatingVendor);
      await createVendor(payload, getToken);
      setMessage("Vendor added.");
      setCreatingVendor(INITIAL_VENDOR);
      setShowCreateModal(false);
      refresh();
    } catch (err) {
      console.error(err);
      setError("Error adding vendor.");
    }
  };

  const handleEdit = (vendor) => {
    setEditingVendor({ ...vendor });
    setOriginalVendor({ ...vendor });
    setMessage("");
    setShowEditModal(true);
  };

  const buildPatch = (current, original) => {
    const cleanedCurrent = cleanPayload(current);
    const cleanedOriginal = cleanPayload(original || {});
    const patch = {};
    const keys = new Set([...Object.keys(cleanedCurrent), ...Object.keys(cleanedOriginal)]);
    keys.forEach((k) => {
      const newVal = k in cleanedCurrent ? cleanedCurrent[k] : null;
      const oldVal = cleanedOriginal[k];
      if (newVal !== oldVal) {
        patch[k] = newVal;
      }
    });
    return patch;
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingVendor?.vendor_id) return;
    setMessage("");
    try {
      const patch = buildPatch(editingVendor, originalVendor);
      await updateVendor(editingVendor.vendor_id, patch, getToken);
      setMessage("Vendor updated.");
      setEditingVendor(null);
      setOriginalVendor(null);
      setShowEditModal(false);
      refresh();
    } catch (err) {
      console.error(err);
      setError("Error updating vendor.");
    }
  };

  const handleDelete = async (vendorId) => {
    if (!window.confirm("Are you sure you want to delete this vendor?")) return;
    setMessage("");
    try {
      await deleteVendor(vendorId, getToken);
      refresh();
    } catch (err) {
      console.error(err);
      setError("Error deleting vendor.");
    }
  };

  const handleOpenLogoModal = (vendor) => {
    setLogoVendor(vendor);
    setLogoFile(null);
    setLogoError("");
    setLogoSuccess("");
    setShowLogoModal(true);
  };

  const handleUploadLogo = async () => {
    if (!logoVendor || !logoFile) {
      setLogoError("Select a PNG file first.");
      return;
    }
    if (logoFile.type !== "image/png") {
      setLogoError("Only PNG files are allowed.");
      return;
    }
    setLogoError("");
    setLogoSuccess("");
    setLogoUploading(true);
    try {
      const { requestLogoUploadUrl, uploadLogoToPresigned } = await import("./api/vendorLogo");
      const presign = await requestLogoUploadUrl(getToken, `${logoVendor.vendor_id}-${Date.now()}.png`);
      await uploadLogoToPresigned(presign.uploadUrl, logoFile);
      setLogoSuccess(presign.publicUrl);
    } catch (err) {
      console.error(err);
      setLogoError(err.message || "Upload failed");
    } finally {
      setLogoUploading(false);
    }
  };

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="m-0">Admin – Vendor Management</h2>
        <button className="btn btn-outline-secondary" onClick={signOut}>
          Sign out
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {message && <div className="alert alert-success">{message}</div>}

      <VendorFilter search={search} setSearch={setSearch} status={status} setStatus={setStatus} />

      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <div className="summary-card shadow-sm">
            <div className="label">Total vendors</div>
            <div className="value">{summary.total}</div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="summary-card shadow-sm">
            <div className="label">Active</div>
            <div className="value text-success">{summary.active}</div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="summary-card shadow-sm">
            <div className="label">Inactive</div>
            <div className="value text-danger">{summary.inactive}</div>
          </div>
        </div>
      </div>

      <VendorAnalyticsPanel getToken={getToken} />

      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="m-0">Vendors</h4>
        <div className="d-flex gap-2">
          <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
            Add Vendor
          </button>
          {loading && <span className="text-muted">Loading...</span>}
        </div>
      </div>

      {paged.length ? (
        <VendorTable vendors={paged} onEdit={handleEdit} onDelete={handleDelete} onUpload={handleOpenLogoModal} />
      ) : (
        <p className="text-muted">No vendors found.</p>
      )}

      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="text-muted">
          Page {page} of {totalPages} — {filtered.length} vendors
        </div>
        <div className="btn-group">
          <button className="btn btn-outline-secondary btn-sm" onClick={() => goToPage(page - 1)} disabled={page === 1}>
            Prev
          </button>
          <button className="btn btn-outline-secondary btn-sm" onClick={() => goToPage(page + 1)} disabled={page === totalPages}>
            Next
          </button>
        </div>
      </div>

      {/* Create Modal */}
      <div className={`modal fade ${showCreateModal ? "show d-block" : ""}`} tabIndex="-1" style={{ background: showCreateModal ? "rgba(0,0,0,0.35)" : "transparent" }}>
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Add Vendor</h5>
              <button type="button" className="btn-close" onClick={() => setShowCreateModal(false)} />
            </div>
            <div className="modal-body">
              <VendorForm
                mode="create"
                vendor={creatingVendor}
                onChange={setCreatingVendor}
                onSubmit={handleCreate}
                onCancel={() => setShowCreateModal(false)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <div className={`modal fade ${showEditModal ? "show d-block" : ""}`} tabIndex="-1" style={{ background: showEditModal ? "rgba(0,0,0,0.35)" : "transparent" }}>
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Update Vendor</h5>
              <button type="button" className="btn-close" onClick={() => { setShowEditModal(false); setEditingVendor(null); setOriginalVendor(null); }} />
            </div>
            <div className="modal-body">
              {editingVendor && (
                <VendorForm
                  mode="edit"
                  vendor={editingVendor}
                  onChange={setEditingVendor}
                  onSubmit={handleUpdate}
                  onCancel={() => { setShowEditModal(false); setEditingVendor(null); setOriginalVendor(null); }}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Logo Modal */}
      <div className={`modal fade ${showLogoModal ? "show d-block" : ""}`} tabIndex="-1" style={{ background: showLogoModal ? "rgba(0,0,0,0.35)" : "transparent" }}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Upload Logo {logoVendor ? `for ${logoVendor.name}` : ""}</h5>
              <button type="button" className="btn-close" onClick={() => setShowLogoModal(false)} />
            </div>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label">PNG file</label>
                <input
                  type="file"
                  accept="image/png"
                  className="form-control"
                  onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                />
                <div className="form-text">Only PNG, uploaded to vendor logo bucket.</div>
              </div>
              {logoError && <div className="alert alert-danger">{logoError}</div>}
              {logoSuccess && (
                <div className="alert alert-success">
                  Uploaded! Public URL:
                  <div className="mt-1"><a href={logoSuccess} target="_blank" rel="noreferrer">{logoSuccess}</a></div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowLogoModal(false)}>
                Close
              </button>
              <button type="button" className="btn btn-primary" onClick={handleUploadLogo} disabled={logoUploading}>
                {logoUploading ? "Uploading..." : "Upload"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
